import { inject, Injectable, provideAppInitializer } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, mergeMap } from 'rxjs';
import * as fromAuth from '../auth/store/auth.reducer';
import { AnalyticsService } from './analytics.service';

/**
 * Service responsible for tracking page views.
 * Subscribes to router navigation events, dedupes on path (not query string per D2),
 * builds route patterns (including custom-matcher route reconstruction per Context #3),
 * and fires analytics events.
 * Root-provided, started via app initializer to avoid blocking bootstrap.
 *
 * Gated on an authenticated user (see `init()`): the heartbeat endpoint this
 * rides (`AnalyticsController`) requires auth (`@UseGuards(AuthGuard)`), so
 * firing on the very first `NavigationEnd` — which happens on /login, before
 * any session exists — would 401 on every load and every subsequent
 * navigation including the redirect back to /login itself, turning a
 * one-off auth failure into a self-sustaining loop. See squadbox-app#1187.
 *
 * That gate sits *ahead* of the dedupe so a navigation dropped for having no
 * user cannot consume its own path's dedupe slot — the first authenticated
 * visit to that path would otherwise be swallowed as a repeat.
 */
@Injectable({ providedIn: 'root' })
export class PageViewTrackingService {
  private readonly router = inject(Router);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly store = inject(Store);
  private started = false;

  /**
   * Initializes the page view tracking service.
   * Subscribes to NavigationEnd events (mirroring AppLoadingService pattern).
   * Skips navigations with no authenticated user — the heartbeat endpoint
   * requires auth, so an anonymous page_viewed call can never succeed.
   * Then dedupes on path (query params do not trigger new page_viewed events
   * per D2); the gate runs first so a dropped navigation leaves the dedupe
   * untouched.
   * Builds route patterns via recursive walk of activated route snapshot tree.
   * Fires sendPageViewedEvent for each unique path.
   * Idempotent: guards a `started` flag so tests can call init() safely.
   */
  init(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    const selectedUser = this.store.selectSignal(
      fromAuth.authFeature.selectUser,
    );

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        map((event) => this.stripQueryAndFragment(event.urlAfterRedirects)),
        filter(() => !!selectedUser()),
        distinctUntilChanged(),
        mergeMap(() => {
          const routePattern = this.buildRoutePattern(
            this.router.routerState.snapshot.root,
          );
          return this.analyticsService.sendPageViewedEvent({
            routePattern,
          });
        }),
      )
      .subscribe({
        error: (error) => {
          // Page view tracking must never block router navigation.

          console.error('[PageViewTrackingService] Error:', error);
        },
      });
  }

  /**
   * Strips query string and fragment from a URL path.
   * E.g., '/chats/conversation/123?sidebar=open#bottom' → '/chats/conversation/123'
   */
  private stripQueryAndFragment(url: string): string {
    const questionIdx = url.indexOf('?');
    const hashIdx = url.indexOf('#');

    if (questionIdx !== -1 && hashIdx !== -1) {
      return url.substring(0, Math.min(questionIdx, hashIdx));
    }
    if (questionIdx !== -1) {
      return url.substring(0, questionIdx);
    }
    if (hashIdx !== -1) {
      return url.substring(0, hashIdx);
    }
    return url;
  }

  /**
   * Builds the route pattern from the activated route snapshot tree.
   * Recursively walks root → firstChild → firstChild, collecting route config paths.
   * For custom-matcher routes (routeConfig.path === undefined), reconstructs the pattern
   * from the snapshot's url segments, replacing bound parameter values with :paramName.
   * Expected outputs: '/login', '/chats/teammates', '/chats/teammates/:slug',
   * '/chats/conversation', '/chats/conversation/:id', '/chats/assistants',
   * '/admin/users', '/admin/groups', '/admin/teammates', '/**' (not-found).
   *
   * Context #3: The /chats/conversation[/:id] route is a custom-matcher route whose
   * routeConfig.path is undefined. This method reconstructs '/chats/conversation' or
   * '/chats/conversation/:id' from the snapshot's url segments + params, avoiding
   * the naive routeConfig.path approach which would incorrectly emit 'undefined'.
   *
   * @param root The root ActivatedRouteSnapshot to start the walk from.
   * @return The reconstructed route pattern (e.g., '/chats/conversation/:id').
   */
  private buildRoutePattern(root: ActivatedRouteSnapshot): string {
    const segments: string[] = [];

    for (
      let route: ActivatedRouteSnapshot | null = root;
      route;
      route = route.firstChild
    ) {
      const config = route.routeConfig;

      // Skip component-less or empty-path configs.
      if (!config || config.path === '') {
        continue;
      }

      // Normal route: collect the literal path.
      if (config.path !== undefined) {
        segments.push(config.path);
        continue;
      }

      // Custom-matcher route: rebuild from url segments + params.
      // Replace any segment whose value is bound in params with :paramName.
      const params = Object.entries(route.params);
      const pattern = route.url
        .map(({ path }) => {
          const bound = params.find(([, value]) => value === path);
          return bound ? `:${bound[0]}` : path;
        })
        .join('/');

      segments.push(pattern);
    }

    return `/${segments.join('/')}`;
  }
}

/**
 * Provides the PageViewTrackingService as a root provider with an app initializer.
 * Wraps provideAppInitializer so the service starts at bootstrap without blocking startup.
 * Follows the exact pattern of provideRefinerInitializer (refiner.service.ts:74-82).
 * Non-fatal: errors in init() are logged but never re-thrown (page view tracking must not block app).
 */
export function providePageViewTrackingInitializer() {
  return provideAppInitializer(() => {
    try {
      inject(PageViewTrackingService).init();
    } catch (error) {
      console.error(
        '[PageViewTrackingInitializer] Failed to initialize:',
        error,
      );
    }
  });
}
